import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Nav from "../components/Nav";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Flame, Sparkles, BookOpen, Target, LogOut, Bell, BellOff, Save, KeyRound } from "lucide-react";

function NotifStatus({ perm, onEnable }) {
  if (perm === "granted") {
    return (
      <div className="brutal-badge bg-brand-mint">
        <Bell className="w-3.5 h-3.5" strokeWidth={3} /> Enabled
      </div>
    );
  }
  if (perm === "denied") {
    return (
      <div className="brutal-badge bg-brand-pink text-white">
        <BellOff className="w-3.5 h-3.5" strokeWidth={3} /> Blocked (change in browser settings)
      </div>
    );
  }
  if (perm === "unsupported") {
    return <div className="brutal-badge bg-white">Not supported on this browser</div>;
  }
  return (
    <button onClick={onEnable} data-testid="notif-enable-btn" className="brutal-btn-mint text-sm py-2">
      <Bell className="w-4 h-4" strokeWidth={3} /> Enable notifications
    </button>
  );
}

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const [stats, setStats] = useState({ due: 0, words_learned: 0, enrolled_count: 0, total_reviews: 0, today_xp: 0 });
  const [dailyGoal, setDailyGoal] = useState(user?.daily_goal || 50);
  const [saving, setSaving] = useState(false);
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data));
  }, []);

  useEffect(() => {
    setDailyGoal(user?.daily_goal || 50);
  }, [user]);

  const saveGoal = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", { daily_goal: parseInt(dailyGoal) });
      setUser(data);
      toast.success("Daily goal updated");
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const askNotif = async () => {
    if (typeof Notification === "undefined") return;
    const perm = await Notification.requestPermission();
    setNotifPerm(perm);
    if (perm === "granted") {
      toast.success("Notifications enabled — you'll be nudged when your streak is at risk.");
      new Notification("LingoFacil", { body: "Streak reminders are on 🔥" });
    } else {
      toast.error("Notifications blocked");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 6) return toast.error("New password must be at least 6 characters");
    if (pwd.next !== pwd.confirm) return toast.error("New passwords don't match");
    if (pwd.next === pwd.current) return toast.error("New password must differ from current");
    setPwdBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: pwd.current,
        new_password: pwd.next,
      });
      if (data?.token) localStorage.setItem("ll_token", data.token);
      toast.success("Password updated");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Change failed");
    } finally {
      setPwdBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen grain-bg" data-testid="profile-page">
      <Nav />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
        <div className="brutal-card p-8 bg-white mb-6">
          <div className="flex items-center gap-6 flex-wrap">
            <div className="w-24 h-24 rounded-2xl border-2 border-brand-ink shadow-brutal bg-brand-blue text-white flex items-center justify-center font-display font-bold text-4xl">
              {user.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-4xl font-bold text-brand-navy">{user.name}</h1>
              <p className="text-brand-navy/70">{user.email}</p>
              <p className="text-sm font-bold text-brand-navy/50 mt-1">
                Joined {new Date(user.created_at).toLocaleDateString()}
              </p>
            </div>
            <button onClick={logout} data-testid="profile-logout-btn" className="brutal-btn-white text-sm py-2">
              <LogOut className="w-4 h-4" strokeWidth={3} /> Log out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="brutal-card p-5 bg-brand-pink/40">
            <Flame className="w-8 h-8 text-brand-navy mb-2" strokeWidth={2.5} />
            <div className="font-display text-3xl font-bold text-brand-navy">{user.streak || 0}</div>
            <div className="text-xs font-bold uppercase text-brand-navy/70">Streak</div>
          </div>
          <div className="brutal-card p-5 bg-brand-yellow">
            <Sparkles className="w-8 h-8 text-brand-navy mb-2" strokeWidth={2.5} />
            <div className="font-display text-3xl font-bold text-brand-navy">{user.xp || 0}</div>
            <div className="text-xs font-bold uppercase text-brand-navy/70">Total XP</div>
          </div>
          <div className="brutal-card p-5 bg-brand-mint/60">
            <BookOpen className="w-8 h-8 text-brand-navy mb-2" strokeWidth={2.5} />
            <div className="font-display text-3xl font-bold text-brand-navy">{stats.words_learned}</div>
            <div className="text-xs font-bold uppercase text-brand-navy/70">Words</div>
          </div>
          <div className="brutal-card p-5 bg-white">
            <Target className="w-8 h-8 text-brand-navy mb-2" strokeWidth={2.5} />
            <div className="font-display text-3xl font-bold text-brand-navy">{stats.due}</div>
            <div className="text-xs font-bold uppercase text-brand-navy/70">Due today</div>
          </div>
        </div>

        {/* Daily goal editor */}
        <div className="brutal-card p-8 bg-white mb-6">
          <h2 className="font-display text-2xl font-bold text-brand-navy mb-2">Daily XP goal</h2>
          <p className="text-brand-navy/70 mb-4">Pick a target that fits your life. You can change it anytime.</p>
          <div className="flex items-center gap-3 flex-wrap">
            {[20, 50, 100, 150, 200].map((g) => (
              <button
                key={g}
                onClick={() => setDailyGoal(g)}
                data-testid={`goal-preset-${g}`}
                className={`brutal-badge px-4 py-2 ${dailyGoal === g ? "bg-brand-yellow" : "bg-white hover:bg-brand-peach"}`}
              >
                {g} XP
              </button>
            ))}
            <input
              type="number"
              min={10}
              max={500}
              value={dailyGoal}
              onChange={(e) => setDailyGoal(e.target.value)}
              data-testid="goal-custom-input"
              className="brutal-input w-28"
            />
            <button onClick={saveGoal} disabled={saving} data-testid="goal-save-btn" className="brutal-btn-pink text-sm py-2">
              <Save className="w-4 h-4" strokeWidth={3} /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {/* Notification settings */}
        <div className="brutal-card p-8 bg-white mb-6">
          <h2 className="font-display text-2xl font-bold text-brand-navy mb-2">Streak reminders</h2>
          <p className="text-brand-navy/70 mb-4">
            Get a friendly nudge in your browser when you haven't practiced today.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <NotifStatus perm={notifPerm} onEnable={askNotif} />
          </div>
        </div>

        {/* Change password */}
        <div className="brutal-card p-8 bg-white mb-6" data-testid="change-password-card">
          <h2 className="font-display text-2xl font-bold text-brand-navy mb-2 flex items-center gap-2">
            <KeyRound className="w-5 h-5" strokeWidth={3} /> Change password
          </h2>
          <p className="text-brand-navy/70 mb-4">Pick something strong. You'll stay logged in on this device.</p>
          <form onSubmit={changePassword} className="space-y-3 max-w-md">
            <input
              type="password"
              required
              value={pwd.current}
              onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
              data-testid="pwd-current"
              placeholder="Current password"
              className="brutal-input"
              autoComplete="current-password"
            />
            <input
              type="password"
              required
              minLength={6}
              value={pwd.next}
              onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
              data-testid="pwd-new"
              placeholder="New password (min 6 chars)"
              className="brutal-input"
              autoComplete="new-password"
            />
            <input
              type="password"
              required
              minLength={6}
              value={pwd.confirm}
              onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
              data-testid="pwd-confirm"
              placeholder="Confirm new password"
              className="brutal-input"
              autoComplete="new-password"
            />
            <button
              type="submit"
              disabled={pwdBusy}
              data-testid="pwd-submit"
              className="brutal-btn-pink text-sm py-2"
            >
              <KeyRound className="w-4 h-4" strokeWidth={3} /> {pwdBusy ? "Updating…" : "Update password"}
            </button>
          </form>
        </div>

        <div className="brutal-card p-8 bg-white">
          <h2 className="font-display text-2xl font-bold text-brand-navy mb-4">Keep going</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/review" className="brutal-btn-pink">Review due cards</Link>
            <Link to="/achievements" className="brutal-btn-yellow">See achievements</Link>
            {user?.is_admin && <Link to="/create" className="brutal-btn-white">Create a deck</Link>}
          </div>
        </div>
      </div>
    </div>
  );
}
